export interface TenantContext {
  subdomain: string;
  usesPathTenantRouting: boolean;
  currentHost: string;
  currentOrigin: string;
}

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "0.0.0.0"]);
const CONFIGURED_TENANT_ROUTING = import.meta.env.VITE_TENANT_ROUTING?.trim().toLowerCase() ?? "";
const CONFIGURED_API_ORIGIN = import.meta.env.VITE_API_ORIGIN?.trim().replace(/\/$/, "") ?? "";
const CONFIGURED_APP_ORIGIN = import.meta.env.VITE_APP_ORIGIN?.trim().replace(/\/$/, "") ?? "";

function getConfiguredCentralHosts() {
  const hosts = new Set<string>();

  for (const origin of [CONFIGURED_API_ORIGIN, CONFIGURED_APP_ORIGIN]) {
    if (!origin) {
      continue;
    }

    try {
      hosts.add(new URL(origin).hostname);
    } catch {}
  }

  return hosts;
}

function getLocalBaseHost(host: string) {
  if (LOCAL_HOSTS.has(host)) {
    return host;
  }

  for (const candidate of LOCAL_HOSTS) {
    if (host.endsWith(`.${candidate}`)) {
      return candidate;
    }
  }

  return "";
}

function usesConfiguredPathRouting() {
  return CONFIGURED_TENANT_ROUTING === "path";
}

function buildOriginFromHost(origin: string, host: string) {
  try {
    const parsed = new URL(origin);
    parsed.host = host;
    return parsed.origin;
  } catch {
    return `https://${host}`;
  }
}

function buildSubdomainOrigin(origin: string, host: string, subdomain: string, apiName: string) {
  const segments = host.split(".");
  if (segments.length < 2) {
    return buildOriginFromHost(origin, `${subdomain}.${host}`);
  }

  const [firstSegment, ...rest] = segments;
  const shouldReplaceFirstSegment = firstSegment === apiName || firstSegment === "www";
  const nextHost = shouldReplaceFirstSegment ? [subdomain, ...rest].join(".") : `${subdomain}.${segments.join(".")}`;
  return buildOriginFromHost(origin, nextHost);
}

export function getTenantContext(location: Location, domainName = "fanvault", apiName = "api"): TenantContext {
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const pathTenant = pathSegments[0] === "t" && pathSegments[1] ? pathSegments[1] : "";
  if (pathTenant) {
    return {
      subdomain: pathTenant,
      usesPathTenantRouting: true,
      currentHost: location.hostname,
      currentOrigin: location.origin,
    };
  }

  const host = location.hostname;
  const segments = host.split(".");
  const firstSegment = segments[0] ?? "";
  const isExactLocalHost = LOCAL_HOSTS.has(host);
  const configuredCentralHosts = getConfiguredCentralHosts();
  const isKnownHost =
    firstSegment === "192" ||
    firstSegment === "localhost" ||
    firstSegment === "127" ||
    firstSegment === "0" ||
    firstSegment === domainName ||
    firstSegment === apiName ||
    configuredCentralHosts.has(host) ||
    segments.includes("ngrok");

  return {
    subdomain: !isExactLocalHost && !isKnownHost && firstSegment ? firstSegment : "",
    usesPathTenantRouting: usesConfiguredPathRouting(),
    currentHost: location.hostname,
    currentOrigin: location.origin,
  };
}

export function buildCentralApiUrl(currentHost: string, currentOrigin = "http://localhost:5173") {
  const localHost = getLocalBaseHost(currentHost);
  if (localHost) {
    const protocol = currentOrigin.startsWith("https://") ? "https" : "http";
    return `${protocol}://${localHost}:8000`;
  }

  if (CONFIGURED_API_ORIGIN) {
    return CONFIGURED_API_ORIGIN;
  }

  return currentOrigin.replace(/\/$/, "");
}

export function buildBaseUrl(context: TenantContext, apiName = "api") {
  const central = buildCentralApiUrl(context.currentHost, context.currentOrigin);
  if (!context.subdomain) {
    return central;
  }

  const localHost = getLocalBaseHost(context.currentHost);
  if (localHost) {
    return `${central}/t/${context.subdomain}`;
  }

  return context.usesPathTenantRouting ? `${central}/t/${context.subdomain}` : buildSubdomainOrigin(central, context.currentHost, context.subdomain, apiName);
}

export function buildTenantApiUrl(location: Location, tenantSlug: string, apiName = "fanvault", domainName = "fanvault") {
  const localHost = getLocalBaseHost(location.hostname);
  if (localHost) {
    const protocol = location.origin.startsWith("https://") ? "https" : "http";
    return `${protocol}://${localHost}:8000/t/${tenantSlug}`;
  }

  const current = getTenantContext(location, domainName, apiName);
  return buildBaseUrl(
    {
      subdomain: tenantSlug,
      usesPathTenantRouting: current.usesPathTenantRouting,
      currentHost: location.hostname,
      currentOrigin: location.origin,
    },
    apiName,
  );
}

export function buildTenantAppUrl(location: Location, tenantSlug: string, domainName = "fanvault") {
  const localHost = getLocalBaseHost(location.hostname);
  if (localHost) {
    return `${location.origin}/t/${tenantSlug}/login`;
  }

  if (usesConfiguredPathRouting()) {
    const origin = CONFIGURED_APP_ORIGIN || location.origin;
    return `${origin}/t/${tenantSlug}/login`;
  }

  if (location.hostname.includes("ngrok")) {
    return `${location.origin}/t/${tenantSlug}/login`;
  }

  const appOrigin = CONFIGURED_APP_ORIGIN || location.origin;
  return buildSubdomainOrigin(appOrigin, new URL(appOrigin).hostname, tenantSlug, domainName);
}
